// src/pages/dashboards/AgentDash.js — Dashboard v2 (mockup-aligned)
//
// Baseado no HTML enviado pelo usuário em maio/26. Mudanças principais
// em relação à v1:
//
//   - Paleta nova (#0A0F1A bg + #0E1525 cards + acentos saturados).
//   - Fonts Outfit (números, display tabular) + Syne (texto). Carregadas
//     no index.html como webfont.
//   - TopBar substituiu os botões flutuantes do SupervisorDash. As ações
//     "Adicionar Agente" / "Conectar Chip Oficial" agora ficam no topo,
//     gated por role + plan_limits. Os modais de criação migraram pra cá.
//   - KPI grid 2.1fr/1fr/1fr/1fr — Saúde como card "hero" (mais largo)
//     com ring SVG à direita do número.
//   - Linhas extras só pra supervisor: Administração Meta (out/26, no
//     lugar do antigo Status do Sistema, que foi pro sino) + TMA + Ranking
//     de agentes + Agentes ao vivo. Agentes simples só veem os KPIs e
//     Templates.
//   - BarChart de Conversão de Templates voltou a ser inline (full-width
//     no rodapé) como no mockup, em vez do modal da v1.
import { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, TextField, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity, FiAlertTriangle, FiTrendingUp, FiFileText,
  FiClock, FiStar, FiUser, FiMail, FiLock, FiArrowUpRight, FiX,
} from 'react-icons/fi';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import AppSidebar from '../../components/AppSidebar';
import TopBar from '../../components/TopBar';
import EmbeddedSignupButton from '../../components/EmbeddedSignupButton';
import api from '../../services/Api';
import usePlanUsage from '../../hooks/usePlanUsage';
import Copiloto from '../../components/copiloto/Copiloto';
import MetaAdminCard from '../../components/metaAdmin/MetaAdminCard';

// ─── Paleta v2 (replicada do mockup HTML) ───────────────────────────────────
// Mantenho tudo num objeto JS único pra evitar drift entre os múltiplos cards.
// Quando precisar tunar a paleta, é um único lugar.
const C = {
  bg:        '#0A0F1A',
  bg2:       '#0E1525',
  bg3:       '#131C30',
  bg4:       '#1A2540',
  border:    'rgba(255,255,255,0.07)',
  border2:   'rgba(255,255,255,0.12)',
  green:     '#00D67C',
  greenDim:  'rgba(0,214,124,0.10)',
  greenGlow: 'rgba(0,214,124,0.22)',
  greenBdr:  'rgba(0,214,124,0.28)',
  red:       '#FF4560',
  redDim:    'rgba(255,69,96,0.12)',
  amber:     '#FFAA00',
  amberDim:  'rgba(255,170,0,0.12)',
  blue:      '#5BA4F5',
  blueDim:   'rgba(91,164,245,0.10)',
  purple:    '#9B7FFF',
  purpleDim: 'rgba(155,127,255,0.12)',
  cyan:      '#00D4E8',
  text:      '#DDE4F0',
  text2:     '#7A8799',
  text3:     '#3E4D60',
};
const FONT_NUM  = "'Outfit', sans-serif";
const FONT_TXT  = "'Syne', sans-serif";

// ─── Helpers visuais ────────────────────────────────────────────────────────

// Header de card: ponto colorido + label uppercase (pattern do mockup).
function CardLabel({ color, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1.2 }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{
        fontFamily: FONT_TXT,
        fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.16em',
        color: C.text3, textTransform: 'uppercase'
      }}>
        {children}
      </Typography>
    </Box>
  );
}

// Tag colorido (▲ +N%) — usado abaixo do número grande pra mostrar variação.
function Tag({ color, bg, children }) {
  return (
    <Box sx={{
      display: 'inline-block',
      fontFamily: FONT_NUM,
      fontSize: '0.62rem', fontWeight: 600,
      px: 0.8, py: 0.25,
      borderRadius: '4px',
      bgcolor: bg, color,
    }}>
      {children}
    </Box>
  );
}

// Card base com a estética do mockup (bg2 + border1px + radius 10).
function Card({ children, sx, hero = false, ...rest }) {
  return (
    <Box
      sx={{
        bgcolor: C.bg2,
        border: `1px solid ${hero ? C.greenBdr : C.border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'rgba(255,255,255,0.11)' },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

// Header do card (linha de título logo abaixo da borda superior).
function CardHead({ icon, title, sub, right }) {
  return (
    <Box sx={{
      px: 2, py: 1.5,
      borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', gap: 1.2,
    }}>
      {icon && <Box sx={{ color: C.green, fontSize: '0.95rem', display: 'flex' }}>{icon}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {sub && (
          <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.1em', color: C.text3, textTransform: 'uppercase' }}>
            {sub}
          </Typography>
        )}
        <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.82rem', fontWeight: 700, color: C.text }}>
          {title}
        </Typography>
      </Box>
      {right}
    </Box>
  );
}

// Ring SVG (saúde) — outline com glow verde.
// `vazio` = ainda não há chip conectado. Sem isso o anel ficava vermelho e
// zerado antes do primeiro número entrar, como se a operação estivesse em
// colapso — quando na verdade ela ainda nem começou.
function HealthRing({ value, vazio = false }) {
  const r = 24;
  const circ = 2 * Math.PI * r;
  const offset = vazio
    ? circ
    : circ - (Math.min(Math.max(value, 0), 100) / 100) * circ;
  const color = vazio ? C.text3 : value >= 80 ? C.green : value >= 50 ? C.amber : C.red;
  return (
    <Box sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', width: 58, height: 58 }}>
      <svg viewBox="0 0 58 58" width="58" height="58">
        <circle cx="29" cy="29" r={r} fill="none" stroke={C.greenDim} strokeWidth="5" />
        <circle
          cx="29" cy="29" r={r} fill="none"
          stroke={color} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{
            transform: 'rotate(-90deg)', transformOrigin: '50% 50%',
            filter: `drop-shadow(0 0 5px ${C.greenGlow})`,
            transition: 'stroke-dashoffset 1.2s ease',
          }}
        />
      </svg>
    </Box>
  );
}

// ─── Faixa de alerta da operação ────────────────────────────────────────────
//
// Out/26. Nasceu do incidente da Pontal Seafood: a Meta bloqueou 137 envios
// em 7 dias e o card exibiu "100%" o tempo inteiro. O backend agora manda
// `device.alerta` (services/health_calc.montar_alerta) e esta faixa é onde
// ele aparece.
//
// Por que faixa colada na base do card, e não um Alert do MUI: o dashboard
// inteiro é um painel de instrumentos (labels tracked, ring SVG, glow). Uma
// caixa arredondada flutuante com ⚠️ lê como "o site deu erro". Colada na
// borda inferior, sem raio e sem respiro, ela lê como uma linha de leitura
// do próprio instrumento — que é o que é.
//
// O ponto na frente do título é o MESMO do CardLabel de propósito: o alerta
// se anuncia na gramática do card, não por cima dela.
// `borda` fecha a contradição visual: o card "hero" tem contorno verde fixo,
// e com uma falha vermelha na base ele dizia as duas coisas ao mesmo tempo —
// "tudo bem" no contorno, "a Meta te bloqueou" no texto. O contorno passa a
// seguir a gravidade. `info` mantém o verde: não é problema do chip.
const NIVEL = {
  risk: { cor: C.red,   wash: 'rgba(255,69,96,0.13)',  glow: 'rgba(255,69,96,.45)',  borda: 'rgba(255,69,96,0.34)'  },
  warn: { cor: C.amber, wash: 'rgba(255,170,0,0.12)',  glow: 'rgba(255,170,0,.40)',  borda: 'rgba(255,170,0,0.32)'  },
  info: { cor: C.blue,  wash: 'rgba(91,164,245,0.10)', glow: 'rgba(91,164,245,.35)', borda: null },
};

function AlertaOperacao({ nivel, titulo, detalhe, acao, chip }) {
  const t = NIVEL[nivel] || NIVEL.info;
  // Só o nível mais grave pulsa. "risk" quer dizer ACONTECENDO AGORA — um
  // ponto parado leria como histórico. Nos outros níveis nada se move.
  const pulsa = nivel === 'risk';
  return (
    <Box sx={{
      borderTop: `1px solid ${t.cor}33`,
      // O degradê é o que faz a faixa parecer ACESA pela falha, em vez de
      // pintada: a cor nasce na régua da esquerda e morre no meio.
      background: `linear-gradient(90deg, ${t.wash} 0%, transparent 58%)`,
      display: 'flex',
      alignItems: 'stretch',
    }}>
      {/* Régua de sinal — a borda esquerda inteira, não um ícone. */}
      <Box sx={{ width: '2px', bgcolor: t.cor, flexShrink: 0, opacity: 0.9 }} />
      <Box sx={{ px: 1.8, py: 1.4, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.6 }}>
          <Box sx={{
            width: 5, height: 5, borderRadius: '50%', bgcolor: t.cor, flexShrink: 0,
            boxShadow: `0 0 5px ${t.glow}`,
            ...(pulsa && {
              animation: 'pulsoAlerta 2.4s ease-in-out infinite',
              '@keyframes pulsoAlerta': {
                '0%, 100%': { opacity: 1,    boxShadow: `0 0 5px ${t.glow}` },
                '50%':      { opacity: 0.35, boxShadow: `0 0 2px ${t.glow}` },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
          }} />
          <Typography sx={{
            fontFamily: FONT_TXT,
            fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: t.cor, lineHeight: 1.2,
          }}>
            {titulo}
          </Typography>
          {/* Só nomeia o chip quando há mais de um — senão é ruído. */}
          {chip && (
            <Typography sx={{
              fontFamily: FONT_TXT, fontSize: '0.58rem', fontWeight: 600,
              color: C.text3, letterSpacing: '0.04em', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              · {chip}
            </Typography>
          )}
        </Box>
        {/* text2, não text3: o text3 (#3E4D60) é penumbra decorativa e some
            no fundo. O que importa precisa ser lido. */}
        <Typography sx={{
          fontFamily: FONT_TXT, fontSize: '0.68rem', fontWeight: 400,
          color: C.text2, lineHeight: 1.5,
        }}>
          {detalhe}
        </Typography>
        {acao && (
          <Typography sx={{
            fontFamily: FONT_TXT, fontSize: '0.66rem', fontWeight: 600,
            color: t.cor, opacity: 0.88, mt: 0.6, lineHeight: 1.4,
          }}>
            {/* Mesma seta dos cards de segmento da landing — vocabulário
                que o produto já usa pra "o que fazer a seguir". */}
            <Box component="span" sx={{ opacity: 0.6, mr: 0.6 }}>→</Box>
            {acao}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Estilo compartilhado: TextField do modal manual "Conectar Chip" ──────
// Todos os campos do modo avançado (credenciais Meta manuais) usam essa
// mesma paleta pra que o texto (label + valor digitado + placeholder)
// tenha contraste consistente no fundo escuro do modal.
//
// O label do MUI tem 3 states distintos que TODOS precisam ser cobertos:
//   1. root (default, quando o input está vazio e desfocado — o texto
//      "Nome do Chip" que aparece no meio do input)
//   2. .Mui-focused (quando o input está ativo)
//   3. .MuiFormLabel-filled (quando tem valor digitado, label sobe/shrunk)
// InputLabelProps.style só cobria o root — os outros 2 caíam no tema
// padrão do MUI e viravam cinza escuro no dark bg. Fix: sx com seletores
// explícitos pra todos os states.
const manualFieldSx = {
  mb: 2,
  // Texto digitado + textarea. WebkitTextFillColor cobre autofill.
  '& .MuiInputBase-input': { color: '#AEB5C1', WebkitTextFillColor: '#AEB5C1' },
  '& textarea': { color: '#AEB5C1' },
  // Placeholder (aparece quando input vazio + focused, dependendo do variant)
  '& .MuiInputBase-input::placeholder': { color: '#AEB5C1', opacity: 0.7 },
  // Label — TODOS os states.
  '& .MuiInputLabel-root': { color: '#AEB5C1' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#AEB5C1' },
  '& .MuiInputLabel-root.MuiFormLabel-filled': { color: '#AEB5C1' },
  // Helper text.
  '& .MuiFormHelperText-root': { color: '#AEB5C1', opacity: 0.75 },
  // Border em cada state pra consistência com paleta clara.
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(174,181,193,0.25)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(174,181,193,0.45)' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(174,181,193,0.65)' },
};

// ─── Componente principal ───────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);

  // Dados
  const [devices, setDevices] = useState([]);
  // Flag pra evitar que o banner empty-state pisque na primeira carga
  // antes do fetchData responder. Sem isso, usePlanUsage (que roda em
  // paralelo) faria canAddChip=true ANTES do devices chegar, mostrando
  // banner por ~200ms até a lista popular. Flip uma vez só na 1ª fetch.
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [healthHistory, setHealthHistory] = useState([]);
  const [clientStats, setClientStats] = useState([]);
  const [templateConversion, setTemplateConversion] = useState([]);
  const [agents, setAgents] = useState([]);
  // Ranking real (do endpoint /users/agents/ranking). Backend agrega
  // OUTBOUND do mês corrente por agente — alimenta o card "Ranking de
  // agentes". Antes era placeholder "—" hardcoded.
  const [agentRanking, setAgentRanking] = useState([]);
  // TMA (Tempo Médio de Resposta AO TEMPLATE) — mede quanto tempo o
  // cliente demora pra responder um template enviado pela empresa.
  // Fonte: /users/agents/tma (janela de 48h; templates sem resposta ignorados).
  // Escopo: todos os chips da empresa; autor pode ser AGENT ou SUPERVISOR.
  const [tma, setTma] = useState(null); // { overall_seconds, best:{}, worst:{} }
  // Volume diário de mensagens (14 pts) — alimenta o card "Volume de
  // mensagens" que substituiu o antigo "Capacidade de disparo". null
  // enquanto carrega; [] ou array de 14 depois.
  const [volume14d, setVolume14d] = useState(null);
  const [stats, setStats] = useState({
    // `null` = ainda não sabemos (carregando ou sem chip). Ver o fetch.
    avgHealth: null, messagesToday: 0, totalActive: 0, inWarmup: 0
  });

  // Modais — migrados do SupervisorDash pra cá pra que os botões da
  // TopBar consigam abri-los sem prop drilling.
  const [openAgentModal, setOpenAgentModal] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentPassword, setAgentPassword] = useState('');
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [msgAgent, setMsgAgent] = useState('');

  // Modal do gráfico de Conversão de Templates — acionado pelo botão
  // "Ver gráfico" do card 4. Igual ao padrão que tínhamos antes, mas o
  // gráfico em si segue idêntico (BarChart Recharts no rodapé).
  const [openTplChartModal, setOpenTplChartModal] = useState(false);

  const [openChipModal, setOpenChipModal] = useState(false);
  // Modo do modal "Conectar Chip" — 'embedded' é o caminho primário desde
  // jun/26 (Embedded Signup Meta), 'manual' é o form legado mantido como
  // fallback "modo avançado" pra quem já tem credenciais Meta na mão.
  const [chipModalMode, setChipModalMode] = useState('embedded');
  const [chipName, setChipName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [chipPin, setChipPin] = useState('');
  const [loadingChip, setLoadingChip] = useState(false);

  // Limites do plano — controla quais botões da TopBar aparecem.
  const { usage, refetch: refetchUsage } = usePlanUsage();

  const canAddAgent = !!usage?.agents?.can_add;
  const canAddChip  = !!usage?.chips?.can_add;

  // Role-gating — supervisor vê linhas extras (Status / TMA / Ranking /
  // Agentes ao vivo). Agentes simples veem só os KPIs + Saldo + Saúde.
  const isSupervisor = user?.role === 'SUPERVISOR' || user?.role === 'ADMIN';

  // Auth boot + fetch loop
  useEffect(() => {
    if (!token || token === 'undefined' || token === 'null') { navigate('/login'); return; }
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      try { setUser(JSON.parse(storedUser)); } catch (e) { localStorage.removeItem('user'); }
    }

    const fetchData = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const resDevices = await api.get('/devices/', config);
        const devs = resDevices.data;
        setDevices(devs);
        setDevicesLoaded(true);

        try {
          const resHistory = await api.get('/health/history', config);
          setHealthHistory(resHistory.data);
        } catch {
          setHealthHistory([
            { day: 'Qui', health: 100 }, { day: 'Sex', health: 100 }, { day: 'Sáb', health: 100 },
            { day: 'Dom', health: 100 }, { day: 'Seg', health: 100 }, { day: 'Ter', health: 100 },
            { day: 'Qua', health: devs.length ? (devs.reduce((a, b) => a + (b.health_score || 100), 0) / devs.length) : 100 }
          ]);
        }

        try {
          const resStats = await api.get('/clients/stats/monthly', config);
          setClientStats(resStats.data);
        } catch {
          setClientStats([]);
        }

        try {
          const resConv = await api.get('/templates/conversion-monthly', config);
          setTemplateConversion(resConv.data || []);
        } catch {
          setTemplateConversion([]);
        }

        // Volume de mensagens dos últimos 14 dias (IN+OUT, todos os chips)
        // — alimenta o card "Volume de mensagens" do row 2.
        try {
          const resVol = await api.get('/messages/volume/last-14d', config);
          setVolume14d(Array.isArray(resVol.data) ? resVol.data : []);
        } catch {
          setVolume14d([]);
        }

        // Lista de agentes — usada no Ranking + Agentes ao vivo (só supervisor)
        try {
          const resAg = await api.get('/users/agents', config);
          setAgents(resAg.data || []);
          // Ranking real (só supervisor consegue chamar — pra agente o
          // backend devolve 403, então capturamos silenciosamente).
          try {
            const resRank = await api.get('/users/agents/ranking?limit=3', config);
            setAgentRanking(resRank.data || []);
          } catch {
            setAgentRanking([]);
          }
          // TMA — também só supervisor; capture 403 silenciosamente
          try {
            const resTma = await api.get('/users/agents/tma', config);
            setTma(resTma.data || null);
          } catch {
            setTma(null);
          }
        } catch {
          setAgents([]);
        }

        // combined_score, NÃO health_score.
        //
        // Out/26: o card mostrava `health_score`, que é só o score interno de
        // comportamento. O backend calcula `combined_score` — interno ×
        // multiplicador de qualidade da Meta, com teto quando a Meta está
        // restringindo o número — e o dashboard jogava esse número fora. Era
        // por isso que a Pontal via 100% enquanto levava 137 bloqueios.
        // Fallback pro health_score cobre resposta de versão antiga da API.
        // Sem chip: `null`, não 0. Com 0 o card mostrava "0%" em VERMELHO,
        // com o ring zerado, enquanto o gráfico logo abaixo mostrava 100 —
        // e a leitura era "sua operação está morrendo" pra quem ainda não
        // conectou o primeiro número. `null` deixa o card mostrar "—".
        const avg = devs.length
          ? (devs.reduce((a, b) => a + (b.combined_score ?? b.health_score ?? 100), 0) / devs.length).toFixed(0)
          : null;
        setStats({
          avgHealth: avg,
          messagesToday: devs.reduce((a, b) => a + (b.messages_today || 0), 0),
          totalActive: devs.length,
          inWarmup: devs.filter(d => d.warmup_days && d.warmup_days < 30).length || 0
        });
      } catch (err) { console.error("Erro ao carregar dados:", err); }
    };

    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [token, navigate]);

  // ─── Handlers de modal ───────────────────────────────────────────────────
  const handleCreateAgent = async () => {
    if (!agentName || !agentEmail || !agentPassword) { setMsgAgent('Preencha todos os campos'); return; }
    setLoadingAgent(true); setMsgAgent('');
    try {
      await api.post('/users/agent',
        { name: agentName, email: agentEmail, password: agentPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsgAgent('Agente criado com sucesso!');
      refetchUsage();
      setTimeout(() => {
        setOpenAgentModal(false);
        setAgentName(''); setAgentEmail(''); setAgentPassword(''); setMsgAgent('');
      }, 1500);
    } catch (err) {
      setMsgAgent(err.response?.data?.detail || 'Erro ao criar agente');
    } finally { setLoadingAgent(false); }
  };

  const handleAddChip = async () => {
    if (!phoneNumber) { alert('Informe o número de telefone do chip.'); return; }
    if (!phoneNumberId || !wabaId || !accessToken) {
      alert('Para chip Business API, preencha Phone Number ID, WABA ID e Access Token.');
      return;
    }
    setLoadingChip(true);
    try {
      const payload = {
        name: chipName || `Chip Oficial ${phoneNumber}`,
        phone_number: phoneNumber.replace(/[^\d]/g, ''),
        phone_number_id: phoneNumberId.trim(),
        waba_id: wabaId.trim(),
        access_token: accessToken.trim(),
      };
      if (chipPin && chipPin.trim()) payload.pin = chipPin.trim();
      await api.post('/devices/', payload, { headers: { Authorization: `Bearer ${token}` } });
      alert('Chip oficial cadastrado com sucesso!');
      setOpenChipModal(false);
      setChipPin('');
      refetchUsage();
      window.location.reload();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || 'Verifique os dados'));
    } finally { setLoadingChip(false); }
  };

  // ─── Derivados ──────────────────────────────────────────────────────────
  // Também por combined_score: o banner "chips com saúde abaixo de 70%"
  // usava o score interno e ficava mudo quando quem derrubava a saúde era a
  // Meta (qualidade rebaixada ou envio restrito).
  const scoreDoChip = (d) => d.combined_score ?? d.health_score ?? 100;
  const highRisk = devices.filter(d => scoreDoChip(d) < 70).slice(0, 5);
  const getHealthColor = (s) => s >= 80 ? C.green : s >= 50 ? C.amber : C.red;
  const chipDots = devices.map(d => {
    const score = d.combined_score ?? d.health_score ?? 100;
    return {
      id: d.id,
      state: score >= 80 ? 'ok' : score >= 50 ? 'warn' : 'risk',
      label: `${d.name}: ${score}%`,
    };
  });

  // O alerta mais grave entre os chips. Um só — a cliente precisa saber o
  // que fazer agora, não ler uma lista de problemas.
  const PESO_ALERTA = { risk: 3, warn: 2, info: 1 };
  const alertaOperacao = devices
    .filter(d => d.alerta)
    .map(d => ({ ...d.alerta, chip: devices.length > 1 ? d.name : null }))
    .sort((a, b) => (PESO_ALERTA[b.nivel] || 0) - (PESO_ALERTA[a.nivel] || 0))[0] || null;

  // Variação real do dia. Era "▲ +0% hoje" hardcoded desde a v2 do
  // dashboard — mostrava "+0%" mesmo com o score despencando.
  // /health/history devolve 7 pontos, o último é hoje.
  const saudeHoje = healthHistory[healthHistory.length - 1]?.health;
  const saudeOntem = healthHistory[healthHistory.length - 2]?.health;
  const deltaSaude = (typeof saudeHoje === 'number' && typeof saudeOntem === 'number')
    ? Math.round(saudeHoje - saudeOntem)
    : null;

  // A cor do número acompanha o valor, como o ring sempre fez. Antes era
  // C.green fixo: um chip a 30% aparecia em verde. Reusa o getHealthColor
  // que já existia no arquivo (e estava sem uso).
  // Sem chip conectado não há saúde: fica cinza, não vermelho.
  const semSaude = stats.avgHealth === null || stats.avgHealth === undefined;
  const corSaude = semSaude ? C.text3 : getHealthColor(parseFloat(stats.avgHealth));

  const tplConvLast = templateConversion[templateConversion.length - 1] || {};
  const tplConvPrev = templateConversion[templateConversion.length - 2] || {};
  const tplConvPct = tplConvLast.enviados > 0
    ? Math.round((tplConvLast.respostas / tplConvLast.enviados) * 100)
    : null;
  const tplConvPrevPct = tplConvPrev.enviados > 0
    ? Math.round((tplConvPrev.respostas / tplConvPrev.enviados) * 100)
    : null;
  const tplConvDelta = (tplConvPct !== null && tplConvPrevPct !== null)
    ? (tplConvPct - tplConvPrevPct) : null;

  // Ranking — usa a lista real de agentes. Por enquanto não temos um
  // endpoint de métricas/contagem por agente, então mostro as 3 primeiras
  // alfabeticamente e o "score" fica como — até o backend expor o número.
  // Usa o ranking real do backend; fallback alfabético se o endpoint
  // ainda não respondeu (carregamento inicial). O score primário agora
  // é `responses_received` (templates que tiveram resposta) — fica
  // alinhado com a regra de "pontuar agente por envio + resposta".
  const ranking = agentRanking.length > 0 ? agentRanking : agents.slice(0, 3).map((a, i) => ({
    id: a.id, name: a.name, email: a.email,
    templates_sent: 0, responses_received: 0, conversion_rate: 0,
    message_count: 0, rank: i + 1,
  }));
  // Pra normalizar a barra de progresso, usamos o máximo de
  // responses_received entre os ranqueados.
  const rankingMax = Math.max(1, ...ranking.map(r => r.responses_received || 0));

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: C.bg, fontFamily: FONT_TXT, color: C.text, overflow: 'hidden' }}>
      <AppSidebar user={user || {}} />

      {/* Main column — sidebar offsetada via CSS var */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          transition: 'margin-left 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* TopBar — substitui os antigos botões flutuantes */}
        <TopBar
          crumbBase="Visão Geral"
          crumbCur="Dashboard"
          canAddAgent={isSupervisor && canAddAgent}
          canAddChip={isSupervisor && canAddChip}
          onAddAgent={() => setOpenAgentModal(true)}
          // Sempre abre no modo Embedded Signup (caminho recomendado).
          // O modal tem link "Tenho credenciais Meta" pra alternar pro form legado.
          onAddChip={() => { setChipModalMode('embedded'); setOpenChipModal(true); }}
        />

        {/* Content rolável */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2, md: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            // scrollbar sutil
            '&::-webkit-scrollbar': { width: 3 },
            '&::-webkit-scrollbar-thumb': { background: C.bg4, borderRadius: 3 },
          }}
        >

          {/* ═══════════════════════════════════════════════════════════
              EMPTY-STATE BANNER — Conectar WhatsApp
              Aparece quando a company não tem nenhum chip ativo. Captura
              os clientes que pularam o Step 4 do Register (clicaram em
              "Conectar depois") ou que apagaram o último chip e precisam
              reconectar. Some assim que o primeiro chip é cadastrado.

              Guarda de visibilidade:
                - devicesLoaded         → garante que a 1ª fetch terminou
                                          (evita flash de 200ms entre usage
                                          carregar e devices popular)
                - devices.length === 0  → só mostra se realmente não tem chip
                - canAddChip            → defensiva pra não mostrar quando o
                  plano não permite adicionar (raríssimo com 0 chips, mas
                  evita banner inútil seguido de erro 403).
          ═══════════════════════════════════════════════════════════ */}
          {devicesLoaded && devices.length === 0 && canAddChip && (
            <Box sx={{
              bgcolor: 'rgba(0,214,124,0.06)',
              border: `1px solid ${C.greenBdr}`,
              borderRadius: '10px',
              p: { xs: 3, md: 4 },
              display: 'flex', flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' }, gap: 3,
            }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                bgcolor: 'rgba(0,214,124,0.15)', border: `2px solid ${C.greenBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.5rem', flexShrink: 0,
              }}>
                📱
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontFamily: FONT_TXT, fontSize: '1.05rem', fontWeight: 800,
                  color: '#fff', mb: 0.6, letterSpacing: '-0.2px',
                }}>
                  Conecte seu WhatsApp para começar
                </Typography>
                <Typography sx={{
                  fontFamily: FONT_TXT, fontSize: '0.82rem', color: C.text2,
                  lineHeight: 1.6, mb: 2, maxWidth: 520,
                }}>
                  Sem chip conectado ainda — sua operação só começa quando você ativa um número. Fluxo oficial Meta, sem campos técnicos. Leva 2 minutos.
                </Typography>
                {/* Botão Embedded Signup inline (componente Fase 2). Erros
                    da Seção 8 da PDF aparecem no próprio componente,
                    propagados do backend humanizados. */}
                <Box sx={{ maxWidth: 420 }}>
                  <EmbeddedSignupButton
                    onSuccess={() => setTimeout(() => window.location.reload(), 1500)}
                    onError={(err) => console.error('Embedded Signup error:', err)}
                  />
                </Box>
                {/* Link discreto pra modo avançado — mesma UX do modal */}
                <Box sx={{ mt: 1.8 }}>
                  <Box
                    component="button"
                    onClick={() => { setChipModalMode('manual'); setOpenChipModal(true); }}
                    sx={{
                      bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                      color: C.text3, fontSize: '0.7rem', fontWeight: 500,
                      fontFamily: FONT_TXT, textDecoration: 'underline',
                      '&:hover': { color: C.text2 },
                    }}
                  >
                    Já tem credenciais Meta? Colar manualmente →
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* ALERTA DE RISCO */}
          {highRisk.length > 0 && (
            <Box sx={{
              bgcolor: 'rgba(255,69,96,0.08)',
              border: '1px solid rgba(255,69,96,0.25)',
              borderRadius: '6px',
              p: '9px 14px',
              display: 'flex', alignItems: 'center', gap: 1.2,
            }}>
              <FiAlertTriangle size={15} color={C.red} />
              <Typography sx={{ fontSize: '0.82rem', color: C.text, fontFamily: FONT_TXT }}>
                <b style={{ color: C.red }}>
                  {highRisk.length} chip{highRisk.length !== 1 ? 's' : ''} com saúde abaixo de 70%
                </b>
                {' '}— {highRisk.map(d => `${d.name} (${scoreDoChip(d)}%)`).join(' · ')}.
                Considere pausa preventiva.
              </Typography>
            </Box>
          )}

          {/* ═══ CETTO COPILOTO (out/26, fase 2 — só leitura) ═══
              Só supervisor (decisão do MVP; o backend recusa agente com
              403). Fica DEPOIS dos avisos acima — chip desconectado e
              chip em risco são mais urgentes que qualquer pergunta — e
              antes dos cards, como no design: o painel expande inline e
              empurra os cards pra baixo, sem esconder nenhum. */}
          {isSupervisor && <Copiloto />}

          {/* ═══ KPI GRID (2.1fr 1fr 1fr 1fr) ═══ */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2.1fr 1fr 1fr 1fr' },
            gap: 1.8,
          }}>

            {/* ── CARD 1: Saúde da Operação (HERO) ── */}
            {/* flex column + o ring dentro do bloco de conteúdo: assim o ring
                centraliza no NÚMERO e não desce junto quando a faixa de
                alerta aparece embaixo. */}
            <Card hero sx={{
              display: 'flex', flexDirection: 'column',
              ...(NIVEL[alertaOperacao?.nivel]?.borda && {
                borderColor: NIVEL[alertaOperacao.nivel].borda,
                // O hover do Card clareia a borda; com falha, manter a cor.
                '&:hover': { borderColor: NIVEL[alertaOperacao.nivel].borda },
              }),
            }}>
              <Box sx={{ position: 'relative', p: 2, pr: '84px', flex: 1 }}>
                {/* O ponto do rótulo segue o número. Fixo em verde, ele era
                    o único elemento do card dizendo "tudo certo" enquanto
                    borda, número, ring e faixa diziam o contrário. */}
                <CardLabel color={corSaude}>Saúde da operação</CardLabel>
                <Typography sx={{
                  fontFamily: FONT_NUM, fontSize: '2.4rem', fontWeight: 700,
                  lineHeight: 1, letterSpacing: '-1px', color: corSaude,
                  transition: 'color .4s ease',
                }}>
                  {semSaude ? '—' : stats.avgHealth}
                  {!semSaude && (
                    <span style={{ fontSize: '1rem', color: C.text3, fontWeight: 400, marginLeft: 1 }}>%</span>
                  )}
                </Typography>
                <Box sx={{ mt: 0.7, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                  {/* Na queda o pill usa a cor do ESTADO, não vermelho fixo:
                      assim ele diz duas coisas — caiu, e o quanto isso é
                      grave. Vermelho fixo num card âmbar significava uma
                      terceira coisa e só confundia. */}
                  {deltaSaude !== null && (
                    <Tag
                      color={deltaSaude < 0 ? corSaude : C.green}
                      bg={deltaSaude < 0 ? `${corSaude}1F` : C.greenDim}
                    >
                      {deltaSaude < 0 ? '▼' : '▲'} {deltaSaude > 0 ? '+' : ''}{deltaSaude}% hoje
                    </Tag>
                  )}
                  <Typography sx={{ fontSize: '0.7rem', color: C.text3, fontFamily: FONT_TXT }}>
                    {devices.length} chip{devices.length !== 1 ? 's' : ''} monitorado{devices.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                {chipDots.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.6, mt: 1.5, flexWrap: 'wrap' }}>
                    {chipDots.map(d => (
                      <Box
                        key={d.id}
                        title={d.label}
                        sx={{
                          width: 9, height: 9, borderRadius: '50%',
                          bgcolor: d.state === 'ok' ? C.green : d.state === 'warn' ? C.amber : C.red,
                          boxShadow: d.state === 'ok'
                            ? `0 0 4px ${C.greenGlow}`
                            : d.state === 'warn'
                              ? '0 0 4px rgba(255,170,0,.35)'
                              : '0 0 4px rgba(255,69,96,.35)',
                          cursor: 'pointer',
                          transition: 'transform 0.15s',
                          '&:hover': { transform: 'scale(1.5)' }
                        }}
                      />
                    ))}
                  </Box>
                )}
                <HealthRing value={parseFloat(stats.avgHealth)} vazio={semSaude} />
              </Box>
              {alertaOperacao && <AlertaOperacao {...alertaOperacao} />}
            </Card>

            {/* ── CARD 2: Mensagens hoje ──
                Contador puro (envios + recebimentos, todos os chips).
                Antes tinha barra `X/300` fake — mensagens freeform não
                têm cap; só templates têm rate-limit anti-abuso, e esse
                fica no card "Volume de mensagens" (row 2) e no card do
                chip em /conversations. Removida a barra e a fração pra
                não induzir cliente a achar que mensagem custa ou é
                capada. */}
            <Card>
              <Box sx={{ p: 2 }}>
                <CardLabel color={C.blue}>Mensagens hoje</CardLabel>
                <Typography sx={{
                  fontFamily: FONT_NUM, fontSize: '1.9rem', fontWeight: 700,
                  lineHeight: 1, letterSpacing: '-1px', color: C.text,
                }}>
                  {stats.messagesToday.toLocaleString('pt-BR')}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.text3, mt: 0.7, fontFamily: FONT_TXT }}>
                  Envios hoje
                </Typography>
                <Typography sx={{
                  fontFamily: FONT_TXT, fontSize: '0.62rem', color: C.text3,
                  mt: 1.3, lineHeight: 1.4,
                }}>
                  Sem limite — só templates têm cap diário.
                </Typography>
              </Box>
            </Card>

            {/* ── CARD 3: Números ativos ── */}
            <Card>
              <Box sx={{ p: 2 }}>
                <CardLabel color={C.green}>Números ativos</CardLabel>
                <Typography sx={{
                  fontFamily: FONT_NUM, fontSize: '1.9rem', fontWeight: 700,
                  lineHeight: 1, letterSpacing: '-1px', color: C.text,
                }}>
                  {stats.totalActive}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.text3, mt: 0.7, fontFamily: FONT_TXT }}>
                  Em operação agora
                </Typography>
                {/* Mesmos estados dos pontinhos do card de saúde. Antes o
                    "ok"/"risco" saía do health_score interno e o "warn" era
                    stats.inWarmup — chips em aquecimento, outro conceito —
                    então as três contas não somavam o total e um chip a 70%
                    aparecia como "ok". */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.3, fontFamily: FONT_NUM, fontSize: '0.66rem' }}>
                  <Box sx={{ color: C.green, fontWeight: 700 }}>{chipDots.filter(d => d.state === 'ok').length} ok</Box>
                  <Box sx={{ color: C.text3 }}>·</Box>
                  <Box sx={{ color: C.amber, fontWeight: 700 }}>{chipDots.filter(d => d.state === 'warn').length} warn</Box>
                  <Box sx={{ color: C.text3 }}>·</Box>
                  <Box sx={{ color: C.red, fontWeight: 700 }}>{chipDots.filter(d => d.state === 'risk').length} risco</Box>
                </Box>
              </Box>
            </Card>

            {/* ── CARD 4: Conversão Templates ── */}
            <Card sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <CardLabel color={C.purple}>Conversão templates</CardLabel>
                <Typography sx={{
                  fontFamily: FONT_NUM, fontSize: '1.9rem', fontWeight: 700,
                  lineHeight: 1, letterSpacing: '-1px', color: C.purple,
                }}>
                  {tplConvPct !== null ? <>{tplConvPct}<span style={{ fontSize: '0.9rem', color: C.text3, fontWeight: 400, marginLeft: 1 }}>%</span></> : '—'}
                </Typography>
                <Box sx={{ mt: 0.7, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {tplConvDelta !== null && (
                    <Tag color={C.purple} bg={C.purpleDim}>
                      {tplConvDelta >= 0 ? '▲ +' : '▼ '}{tplConvDelta}%
                    </Tag>
                  )}
                  <Typography sx={{ fontSize: '0.7rem', color: C.text3, fontFamily: FONT_TXT }}>
                    vs mês anterior
                  </Typography>
                </Box>

                {/* Botão "Ver gráfico" — abre o modal com o BarChart histórico
                    (mesmo gráfico que ficava inline no rodapé). Posicionado
                    no fim do card via mt:auto pra alinhar com a base dos
                    outros cards do row. */}
                <Button
                  size="small"
                  onClick={() => setOpenTplChartModal(true)}
                  endIcon={<FiArrowUpRight size={12} />}
                  sx={{
                    alignSelf: 'flex-start', mt: 'auto', pt: 1.3,
                    textTransform: 'none',
                    fontFamily: FONT_TXT,
                    fontSize: '0.68rem', fontWeight: 700,
                    color: C.purple,
                    bgcolor: C.purpleDim,
                    border: '1px solid transparent',
                    borderRadius: '6px',
                    px: 1.2, py: 0.5,
                    minHeight: 0, lineHeight: 1.2,
                    '&:hover': { bgcolor: 'rgba(155,127,255,0.2)', borderColor: 'rgba(155,127,255,0.35)' }
                  }}
                >
                  Ver gráfico
                </Button>
              </Box>
            </Card>

          </Box>

          {/* ═══ ROW 2: Volume de mensagens + Evolução da Saúde (1fr 1fr) ═══ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.8 }}>

            {/* VOLUME DE MENSAGENS — número grande dos últimos 7d, delta %
                vs semana anterior, e sparkline dual-series com os 14 dias
                (anterior tracejada + atual em área verde). Substituiu o
                antigo "Capacidade de disparo" — o cap por chip virou
                rate-limit anti-abuso silencioso após o refactor de ledger
                (ago/26), então parou de valer o espaço nobre do dashboard;
                volume operacional é a métrica que o dono realmente olha
                pra sentir se a operação está aquecendo ou esfriando. */}
            <Card>
              <CardHead
                icon={<FiActivity size={14} color={C.green} />}
                title="Volume de mensagens"
                sub="Últimos 7 dias"
              />
              <Box sx={{ p: 2 }}>
                <VolumeSparkline data={volume14d} />
              </Box>
            </Card>

            {/* EVOLUÇÃO DA SAÚDE 7 DIAS — barras coloridas com altura honesta */}
            <Card>
              <CardHead
                icon={<FiTrendingUp size={14} color={C.green} />}
                title="Evolução da Saúde"
                right={<Typography sx={{ fontSize: '0.66rem', color: C.text3, fontFamily: FONT_TXT }}>Últimos 7 dias</Typography>}
              />
              <Box sx={{ p: 2 }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.7,
                }}>
                  {healthHistory.map((d, i) => {
                    const h = Math.round(d.health);
                    const isOk = h >= 80;
                    const isWarn = h >= 50 && h < 80;
                    return (
                      <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                          width: '100%',
                          // barras de altura idêntica visualmente (80px),
                          // diferenciação fica nas cores conforme saúde
                          height: '90px',
                          borderRadius: '3px',
                          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                          fontFamily: FONT_NUM, fontSize: '0.6rem', fontWeight: 600,
                          color: isOk ? C.green : isWarn ? C.amber : C.red,
                          bgcolor: isOk ? C.greenDim : isWarn ? C.amberDim : C.redDim,
                          border: `1px solid ${isOk ? C.greenBdr : isWarn ? 'rgba(255,170,0,0.3)' : 'rgba(255,69,96,0.3)'}`,
                          py: 0.5,
                        }}>
                          {h}%
                        </Box>
                        <Typography sx={{ fontSize: '0.6rem', color: C.text3, fontFamily: FONT_TXT }}>
                          {d.day}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Card>

          </Box>


          {/* ═══ ROW 3 (supervisor): Administração Meta (2fr) + side panels (TMA + Ranking) (1fr) ═══ */}
          {isSupervisor && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.8 }}>

              {/* ADMINISTRAÇÃO META — out/26, substitui o "Status do sistema"
                  (roadmap estático, removido). Mesma
                  altura da coluna vizinha: no desktop o cartão é absoluto
                  dentro da célula, então quem dita a altura é TMA + Ranking
                  e nenhuma aba estica a linha (documento, Estrutura).
                  No celular/tablet o cartão tem 460px de MÍNIMO e cresce com
                  o conteúdo: com altura fixa, o Teto bloqueado com nome de
                  chip comprido cortava 24px e a Franquia 2px (varredura de
                  out/26 em 320–414px). `minWidth: 0`: sem ele a coluna do
                  grid não encolhia abaixo de 382px (o mínimo do conteúdo do
                  cartão) e, em celular até 414px, o cartão passava da tela e
                  a área do dashboard rolava de lado. */}
              <Box sx={{ position: 'relative', minWidth: 0, minHeight: { xs: 460, lg: 0 } }}>
                <Box sx={{ position: { xs: 'relative', lg: 'absolute' }, inset: { lg: 0 }, display: 'flex', flexDirection: 'column', minHeight: { xs: 460, lg: 0 } }}>
                  <MetaAdminCard onConectar={() => { setChipModalMode('embedded'); setOpenChipModal(true); }} />
                </Box>
              </Box>

              {/* SIDE: TMA + Ranking */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>

                {/* TMA — Tempo médio de resposta AO TEMPLATE (cliente respondendo
                    OUTBOUND do tipo "Template: ...", janela de 48h). Endpoint
                    /users/agents/tma agrega todos os chips da empresa; melhor/
                    pior computados sobre o `user_id` de quem enviou o template
                    (inclui SUPERVISOR). */}
                <Card>
                  <CardHead
                    icon={<FiClock size={14} color={C.blue} />}
                    title="Tempo médio de resposta"
                    sub="Cliente respondendo ao template · 48h"
                  />
                  <Box sx={{ p: 2 }}>
                    {(() => {
                      // Formata segundos → "Xm Ys" / "Ys" / "—"
                      const fmt = (sec) => {
                        if (sec === null || sec === undefined) return null;
                        const s = Math.round(sec);
                        if (s < 60) return { val: `${s}`, unit: 's' };
                        const m = Math.floor(s / 60);
                        const rem = s % 60;
                        return { val: `${m}`, unit: rem > 0 ? `m ${rem}s` : 'm' };
                      };
                      const overall = fmt(tma?.overall_seconds);
                      const best = fmt(tma?.best?.avg_seconds);
                      const worst = fmt(tma?.worst?.avg_seconds);
                      const withinGoal = tma?.overall_seconds !== null
                        && tma?.overall_seconds !== undefined
                        && tma.overall_seconds < 300; // 5 min em segundos

                      return (
                        <>
                          <Box sx={{ bgcolor: C.bg3, borderRadius: '6px', p: '12px 14px', mb: 1.2 }}>
                            {overall ? (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                  <Typography sx={{ fontFamily: FONT_NUM, fontSize: '1.7rem', fontWeight: 700, lineHeight: 1, color: C.text }}>
                                    {overall.val}
                                  </Typography>
                                  <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.85rem', color: C.text3, fontWeight: 400, ml: 0.4 }}>
                                    {overall.unit}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.8 }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: C.text3, fontFamily: FONT_TXT }}>
                                    Meta: &lt; 5 min
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, fontFamily: FONT_TXT, color: withinGoal ? C.green : C.amber }}>
                                    {withinGoal ? '▲ Dentro da meta' : '▼ Fora da meta'}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <Typography sx={{ fontFamily: FONT_NUM, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2, color: C.text3 }}>
                                — <span style={{ fontSize: '0.78rem', fontWeight: 400 }}>sem dados no mês</span>
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.8 }}>
                            {[
                              { lbl: 'Melhor agente', val: best, color: C.green, name: tma?.best?.name },
                              { lbl: 'Pior agente',   val: worst, color: C.amber, name: tma?.worst?.name },
                            ].map(item => (
                              <Box key={item.lbl} sx={{ flex: 1, bgcolor: C.bg3, borderRadius: '6px', p: '8px 10px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.55rem', color: C.text3, fontFamily: FONT_TXT, mb: 0.2 }}>
                                  {item.lbl}
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.88rem', fontWeight: 700, color: item.val ? item.color : C.text3 }}>
                                  {item.val ? <>{item.val.val}<span style={{ fontSize: '0.66rem', fontWeight: 400 }}>{item.val.unit}</span></> : '—'}
                                </Typography>
                                {item.name && (
                                  <Typography sx={{ fontSize: '0.6rem', color: C.text3, fontFamily: FONT_TXT, mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.name}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                </Card>

                {/* RANKING DE AGENTES — só supervisor enxerga.
                    Score: templates enviados que receberam resposta do
                    cliente (este mês). Ordenado por respostas DESC.  */}
                <Card>
                  <CardHead
                    icon={<FiStar size={14} color={C.amber} />}
                    title="Ranking de agentes"
                    sub="Templates com resposta"
                    right={<Typography sx={{ fontSize: '0.62rem', color: C.text3, fontFamily: FONT_TXT }}>Este mês</Typography>}
                  />
                  <Box sx={{ px: 2, py: 1 }}>
                    {ranking.length === 0 ? (
                      <Typography sx={{ textAlign: 'center', py: 2, fontSize: '0.72rem', color: C.text3, fontFamily: FONT_TXT }}>
                        Nenhum agente cadastrado ainda.
                      </Typography>
                    ) : ranking.map((ag, i) => {
                      // Cores fixas pelos 3 primeiros lugares; barras
                      // proporcionais a `responses_received` (top1 = 100%).
                      const colors = [C.green, C.blue, C.purple];
                      const sent = ag.templates_sent || 0;
                      const resp = ag.responses_received || 0;
                      const rate = ag.conversion_rate || 0;
                      const w = Math.max(8, Math.round((resp / rankingMax) * 100));
                      return (
                        <Box key={ag.id} sx={{
                          display: 'flex', alignItems: 'center', gap: 1.2,
                          py: 1, borderBottom: i < ranking.length - 1 ? `1px solid ${C.border}` : 'none',
                        }}>
                          <Typography sx={{
                            fontFamily: FONT_NUM, fontSize: '0.7rem', fontWeight: 700,
                            color: i === 0 ? '#FFD700' : C.text3,
                            width: 14, textAlign: 'center', flexShrink: 0,
                          }}>
                            {i + 1}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: C.text, mb: 0.3, fontFamily: FONT_TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ag.name}
                            </Typography>
                            {/* Subtítulo discreto: respostas / enviados · taxa */}
                            <Typography sx={{
                              fontFamily: FONT_NUM, fontSize: '0.6rem', color: C.text3,
                              mb: 0.35,
                            }}>
                              {resp}/{sent} resp · {rate}%
                            </Typography>
                            <Box sx={{ height: 3, bgcolor: C.bg4, borderRadius: 2, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${w}%`, bgcolor: colors[i] || C.text2, transition: 'width 1s ease' }} />
                            </Box>
                          </Box>
                          {/* Score primário: respostas recebidas */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                            <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.85rem', fontWeight: 800, color: colors[i] || C.text2, lineHeight: 1 }}>
                              {resp > 0 ? resp.toLocaleString('pt-BR') : '—'}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.55rem', color: C.text3, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mt: 0.1 }}>
                              respostas
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Card>

              </Box>
            </Box>
          )}

          {/* ═══ ROW 4 (supervisor): Crescimento Contatos (2fr) + Agentes ao vivo (1fr) ═══ */}
          {isSupervisor && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.8 }}>

              {/* Crescimento Contatos — AreaChart com Recharts */}
              <Card>
                <CardHead
                  icon={<FiTrendingUp size={14} color={C.green} />}
                  title="Crescimento de contatos"
                  sub="Clientes únicos contactados por mês"
                  right={
                    clientStats.length > 0 ? (
                      <Tag color={C.green} bg={C.greenDim}>
                        {clientStats[clientStats.length - 1]?.clientes || 0} este mês
                      </Tag>
                    ) : null
                  }
                />
                <Box sx={{ p: 2 }}>
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={clientStats} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorClientes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={C.green} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: C.text3, fontFamily: 'Syne' }} dy={8} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: C.text3 }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 8, border: `1px solid ${C.border}`,
                            background: C.bg2, color: C.text, fontFamily: 'Syne',
                          }}
                          formatter={(v) => [`${v} clientes`, 'Contactados']}
                        />
                        <Area type="monotone" dataKey="clientes" stroke={C.green} strokeWidth={2.5}
                          fillOpacity={1} fill="url(#colorClientes)"
                          dot={{ fill: C.green, r: 3 }}
                          activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Card>

              {/* Agentes ao vivo */}
              <Card>
                <CardHead
                  icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: C.green }} />}
                  title="Agentes ao vivo"
                  right={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tag color={C.green} bg={C.greenDim}>{agents.length} online</Tag>
                    </Box>
                  }
                />
                <Box sx={{ px: 2, py: 1 }}>
                  {agents.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', py: 2, fontSize: '0.72rem', color: C.text3, fontFamily: FONT_TXT }}>
                      Nenhum agente cadastrado.
                    </Typography>
                  ) : agents.slice(0, 5).map((ag, i) => {
                    const initials = (ag.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
                    return (
                      <Box key={ag.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.2,
                        py: 1, borderBottom: i < Math.min(agents.length, 5) - 1 ? `1px solid ${C.border}` : 'none',
                      }}>
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          bgcolor: C.greenDim, color: C.green,
                          border: `1px solid ${C.greenBdr}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.62rem', fontWeight: 700, fontFamily: FONT_TXT,
                          flexShrink: 0,
                        }}>
                          {initials}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: C.text, fontFamily: FONT_TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ag.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: C.text3, mt: 0.1, fontFamily: FONT_TXT }}>
                            {ag.email}
                          </Typography>
                        </Box>
                        <Tag color={C.green} bg={C.greenDim}>Online</Tag>
                      </Box>
                    );
                  })}
                </Box>
              </Card>

            </Box>
          )}

          {/* ROW 5 (BarChart inline) foi removido — o mesmo gráfico agora
              abre via modal pelo botão "Ver gráfico" do card 4. Mantemos
              o BarChart idêntico (mesmas cores, legenda e tooltip), só
              mudou o container. */}

        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════
          MODAL: Conversão de Templates (BarChart histórico)
          Mesmo gráfico que ficava inline no rodapé. Não duplicamos a
          implementação — é o único lugar agora.
      ═══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={openTplChartModal}
        onClose={() => setOpenTplChartModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: C.bg2,
            border: `1px solid ${C.border}`,
            borderRadius: '10px',
            color: C.text,
          }
        }}
      >
        <DialogTitle sx={{
          p: 2, pb: 1.2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              bgcolor: C.purpleDim,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FiFileText size={15} color={C.purple} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.95rem', fontWeight: 700, color: C.text, lineHeight: 1.1 }}>
                Templates — Conversão
              </Typography>
              <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.7rem', color: C.text3 }}>
                Templates enviados x respostas recebidas, mês a mês
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpenTplChartModal(false)} sx={{ color: C.text3 }} size="small">
            <FiX size={16} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, pt: 1 }}>
          {tplConvPct !== null && (
            <Box sx={{ mb: 1.5 }}>
              <Tag color={C.purple} bg={C.purpleDim}>
                {tplConvPct}% de conversão este mês
              </Tag>
            </Box>
          )}

          {/* Legenda manual (igual a versão inline anterior — não usei a
              <Legend> do Recharts pra manter o estilo customizado) */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: C.green }} />
              <Typography sx={{ fontSize: '0.72rem', color: C.text2, fontFamily: FONT_TXT }}>Templates enviados</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: C.purple }} />
              <Typography sx={{ fontSize: '0.72rem', color: C.text2, fontFamily: FONT_TXT }}>Respostas dos clientes</Typography>
            </Box>
          </Box>

          <Box sx={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={templateConversion} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: C.text3 }} dy={8} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: C.text3 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8, border: `1px solid ${C.border}`,
                    background: C.bg2, color: C.text, fontFamily: 'Syne',
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="enviados" name="Templates enviados" fill={C.green} radius={[4, 4, 0, 0]} />
                <Bar dataKey="respostas" name="Respostas dos clientes" fill={C.purple} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Typography sx={{
            fontSize: '0.7rem', color: C.text3, mt: 2, pt: 1.5,
            borderTop: `1px solid ${C.border}`,
            fontFamily: FONT_TXT, lineHeight: 1.6,
          }}>
            Este gráfico compara, mês a mês, a quantidade de templates HSM enviados pelos seus chips
            e quantos desses contatos responderam. Use-o para identificar quais meses e quais
            mensagens convertem melhor.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════
          MODAL: Criar Agente (era flutuante no SupervisorDash)
      ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={openAgentModal} onClose={() => setOpenAgentModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#075e54', color: 'white', fontWeight: 'bold', pr: 10 }}>
          Novo Agente
          <Button onClick={() => setOpenAgentModal(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>×</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 4 }}>
          <TextField autoFocus label="Nome do Agente" fullWidth value={agentName} onChange={e => setAgentName(e.target.value)} sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiUser size={16} color="#94a3b8" /></InputAdornment> }} />
          <TextField label="E-mail" type="email" fullWidth value={agentEmail} onChange={e => setAgentEmail(e.target.value)} sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiMail size={16} color="#94a3b8" /></InputAdornment> }} />
          <TextField label="Senha" type="password" fullWidth value={agentPassword} onChange={e => setAgentPassword(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiLock size={16} color="#94a3b8" /></InputAdornment> }} />
          {msgAgent && <Alert severity={msgAgent.includes('sucesso') ? 'success' : 'error'} sx={{ mt: 2 }}>{msgAgent}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={() => setOpenAgentModal(false)} disabled={loadingAgent}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateAgent} disabled={loadingAgent}
            sx={{ bgcolor: '#25d366', px: 5, '&:hover': { bgcolor: '#16a34a' } }}>
            {loadingAgent ? 'Criando...' : 'Criar Agente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════
          MODAL: Conectar Chip — Embedded Signup primário + form legado
                 como fallback. Caminho default é Embedded Signup (Meta
                 oficial, sem campos técnicos). O link no rodapé "Tenho
                 credenciais Meta" alterna pro form antigo pra clientes
                 que já têm Phone Number ID / WABA ID / Access Token na
                 mão e querem colar manualmente.

                 Erros críticos da PDF Seção 8 são tratados no backend
                 (_humanize_meta_error em onboarding.py) e propagados pelo
                 EmbeddedSignupButton através do err.response.data.detail.
                 Inclui: número em outra plataforma, número pessoal, código
                 expirado, sem permissão BM, sessão expirada, limite plano.
      ═══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={openChipModal}
        onClose={() => setOpenChipModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: C.bg2,
            border: `1px solid ${C.border}`,
            borderRadius: '10px',
            color: C.text,
          }
        }}
      >
        <DialogTitle sx={{
          p: 2.5, pb: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <Box>
            <Typography sx={{ fontFamily: "'Syne',sans-serif", fontSize: '1.1rem', fontWeight: 700, color: '#fff', mb: 0.3 }}>
              Conectar WhatsApp
            </Typography>
            <Typography sx={{ fontFamily: "'Syne',sans-serif", fontSize: '0.72rem', color: C.text2 }}>
              {chipModalMode === 'embedded'
                ? 'Fluxo oficial Meta — sem precisar de credenciais técnicas'
                : 'Modo avançado — cole as credenciais Meta manualmente'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenChipModal(false)} sx={{ color: C.text3, '&:hover': { color: '#fff' } }} size="small">
            <FiX size={16} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {chipModalMode === 'embedded' ? (
            /* ──────── MODO EMBEDDED SIGNUP (primário) ──────── */
            <Box>
              {/* Cenários — mesmo padrão do Step 4 do Register. Eram 3 até
                  set/26; o "Cenário 3: Chip Gerenciado Cetto" saiu junto com
                  o produto, e o grid caiu pra 2 colunas — com `1fr 1fr 1fr` e
                  dois filhos a terceira coluna fica vazia. */}
              <Box sx={{
                display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5, mb: 2.5,
              }}>
                {[
                  { num: 1, color: '#5BA4F5', bg: 'rgba(91,164,245,0.10)', bdr: 'rgba(91,164,245,0.25)',
                    title: 'Sem conta na Meta', desc: 'Meta cria um BM automaticamente — você só faz login.' },
                  { num: 2, color: '#9B7FFF', bg: 'rgba(155,127,255,0.10)', bdr: 'rgba(155,127,255,0.25)',
                    title: 'Já tem Meta Business', desc: 'Lista seus números — você escolhe qual usar.' },
                ].map(sc => (
                  <Box key={sc.num} sx={{
                    bgcolor: C.bg3,
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px', p: '11px 13px',
                  }}>
                    <Box sx={{
                      display: 'inline-block', mb: 0.8,
                      fontFamily: "'Outfit',sans-serif", fontSize: '0.55rem', fontWeight: 700,
                      px: 0.8, py: 0.15, borderRadius: '4px',
                      bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.bdr}`,
                    }}>
                      Cenário {sc.num}
                    </Box>
                    <Typography sx={{ fontFamily: "'Syne',sans-serif", fontSize: '0.68rem', fontWeight: 700, color: '#fff', mb: 0.3 }}>
                      {sc.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: C.text3, lineHeight: 1.5, fontFamily: "'Syne',sans-serif" }}>
                      {sc.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Botão Embedded Signup (Fase 2) — erros aparecem no próprio
                  componente, propagados do backend já humanizados. */}
              <Box sx={{ mb: 2 }}>
                <EmbeddedSignupButton
                  onSuccess={(device) => {
                    // Fecha o modal e reload pra refletir o chip novo na grid.
                    // Reload total (em vez de refetch) preserva paridade com
                    // o handleAddChip antigo + zera state local do modal.
                    setOpenChipModal(false);
                    setTimeout(() => window.location.reload(), 1500);
                  }}
                  onError={(err) => {
                    // Erro já foi exibido pelo próprio componente. Só log
                    // pra debug; não fazemos nada adicional aqui (UX limpa).
                    console.error('Embedded Signup error:', err);
                  }}
                />
              </Box>

              {/* Alertas da PDF Seção 4 (passo 6 crítico) */}
              <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.2,
                bgcolor: 'rgba(255,170,0,0.12)', border: '1px solid rgba(255,170,0,0.30)',
                borderRadius: '7px', p: '10px 13px', mb: 1.2,
              }}>
                <Typography sx={{ fontSize: '0.95rem' }}>⚠️</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.text2, lineHeight: 1.6, fontFamily: "'Syne',sans-serif" }}>
                  <Box component="strong" sx={{ color: '#FFAA00' }}>Atenção:</Box> ao registrar um número, ele será desvinculado do WhatsApp pessoal ou Business App. Recomendamos um número exclusivo pra empresa.
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.2,
                bgcolor: 'rgba(91,164,245,0.10)', border: '1px solid rgba(91,164,245,0.25)',
                borderRadius: '7px', p: '10px 13px',
              }}>
                <Typography sx={{ fontSize: '0.95rem' }}>ℹ️</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: C.text2, lineHeight: 1.6, fontFamily: "'Syne',sans-serif" }}>
                  <Box component="strong" sx={{ color: '#5BA4F5' }}>Número em outra plataforma?</Box> Se está em Wati, BotConversa ou outra API, fale com o suporte pra migrar gratuitamente.
                </Typography>
              </Box>

              {/* Link pra fallback (modo manual) — discreto no rodapé */}
              <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.68rem', color: C.text3, fontFamily: "'Syne',sans-serif", mb: 0.5 }}>
                  Já tem credenciais Meta na mão?
                </Typography>
                <Box
                  component="button"
                  onClick={() => setChipModalMode('manual')}
                  sx={{
                    bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                    color: C.text2, fontSize: '0.72rem', fontWeight: 600,
                    fontFamily: "'Syne',sans-serif", textDecoration: 'underline',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  Modo avançado — colar Phone Number ID / WABA / Access Token →
                </Box>
              </Box>
            </Box>
          ) : (
            /* ──────── MODO MANUAL (form legado, preservado) ──────── */
            <Box>
              <Box sx={{
                bgcolor: 'rgba(91,164,245,0.06)', border: '1px solid rgba(91,164,245,0.20)',
                borderRadius: '7px', p: '10px 13px', mb: 2.5,
              }}>
                <Typography sx={{ fontSize: '0.72rem', color: C.text2, lineHeight: 1.55, fontFamily: "'Syne',sans-serif" }}>
                  Use este modo apenas se você já tem WABA ID, Phone Number ID e Access Token gerados no Meta Business Manager.
                  Caso contrário,{' '}
                  <Box component="span"
                    onClick={() => setChipModalMode('embedded')}
                    sx={{ color: '#5BA4F5', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}
                  >
                    use o Embedded Signup
                  </Box>
                  {' '}— mais rápido e sem risco de erro.
                </Typography>
              </Box>

              {/* Fields do modal manual: label + placeholder + valor digitado
                  em #AEB5C1 pra dar contraste consistente no fundo escuro
                  (padrão do modal segue tema dark do AgentDash). sx
                  compartilhado em `manualFieldSx` (top-level do módulo). */}
              <TextField label="Nome do Chip (opcional)" fullWidth value={chipName} onChange={e => setChipName(e.target.value)} sx={manualFieldSx} InputLabelProps={{ style: { color: '#AEB5C1' } }} />
              <TextField label="Número com DDD (+55...)" fullWidth value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} sx={manualFieldSx} InputLabelProps={{ style: { color: '#AEB5C1' } }} />
              <TextField label="Phone Number ID" fullWidth value={phoneNumberId} onChange={e => setPhoneNumberId(e.target.value)} sx={manualFieldSx} InputLabelProps={{ style: { color: '#AEB5C1' } }} />
              <TextField label="WABA ID" fullWidth value={wabaId} onChange={e => setWabaId(e.target.value)} sx={manualFieldSx} InputLabelProps={{ style: { color: '#AEB5C1' } }} />
              <TextField label="Access Token" fullWidth multiline rows={4} value={accessToken} onChange={e => setAccessToken(e.target.value)} sx={manualFieldSx} InputLabelProps={{ style: { color: '#AEB5C1' } }} />
              <TextField
                label="PIN do 2FA (6 dígitos) — opcional"
                fullWidth
                value={chipPin}
                onChange={e => setChipPin(e.target.value.replace(/[^\d]/g, '').slice(0, 8))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8 }}
                helperText="Se informado, o número é registrado automaticamente na Meta e fica pronto para enviar."
                sx={manualFieldSx}
                InputLabelProps={{ style: { color: '#AEB5C1' } }}
                FormHelperTextProps={{ style: { color: '#AEB5C1', opacity: 0.75 } }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${C.border}`, gap: 1 }}>
          {chipModalMode === 'manual' && (
            <Button
              onClick={() => setChipModalMode('embedded')}
              sx={{ textTransform: 'none', color: C.text3, fontFamily: "'Syne',sans-serif", mr: 'auto' }}
            >
              ← Voltar pro Embedded Signup
            </Button>
          )}
          <Button onClick={() => setOpenChipModal(false)} sx={{ textTransform: 'none', color: C.text3, fontFamily: "'Syne',sans-serif" }}>
            Cancelar
          </Button>
          {chipModalMode === 'manual' && (
            <Button
              variant="contained"
              onClick={handleAddChip}
              disabled={loadingChip}
              sx={{
                bgcolor: C.green, color: C.bg, px: 4, textTransform: 'none', fontWeight: 700,
                fontFamily: "'Syne',sans-serif", borderRadius: '7px',
                '&:hover': { bgcolor: '#00f090' },
              }}
            >
              {loadingChip ? 'Cadastrando...' : 'Cadastrar Chip'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── VolumeSparkline ────────────────────────────────────────────────────
// Card "Volume de mensagens": número grande dos últimos 7d + delta %
// contra os 7d anteriores + área sparkline de 14 pontos. As duas séries
// são renderizadas separadas — a anterior fica em stroke discreto
// tracejado (sem fill), a atual em stroke sólido + gradient — pra que
// o gráfico já conte a história do delta sem depender só do pill.
//
// `data` chega do endpoint /messages/volume/last-14d como
//   [{ date:'2026-08-08', count:42 }, …, { date:'2026-08-21', count:87 }]
// em ordem cronológica (mais antigo → hoje).
function VolumeSparkline({ data }) {
  const loading = data === null || data === undefined;
  const points = Array.isArray(data) ? data : [];
  const prev7 = points.slice(0, 7);
  const cur7  = points.slice(7, 14);
  const total = cur7.reduce((a, p) => a + (p.count || 0), 0);
  const prevTotal = prev7.reduce((a, p) => a + (p.count || 0), 0);
  const delta = prevTotal > 0
    ? Math.round(((total - prevTotal) / prevTotal) * 100)
    : null;
  const isEmpty = !loading && total === 0 && prevTotal === 0;

  // Overlap intencional no índice 7: as duas séries compartilham o ponto
  // de fronteira pra que a linha anterior "encoste" no início da área
  // atual — sem isso, ficaria um gap visual entre elas.
  const chartData = points.map((p, i) => ({
    idx: i,
    date: p.date,
    prev: i <= 7 ? (p.count || 0) : null,
    cur:  i >= 7 ? (p.count || 0) : null,
  }));

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{
          fontFamily: FONT_NUM, fontSize: '2.4rem', fontWeight: 700,
          color: C.text, lineHeight: 1, letterSpacing: '-0.02em',
        }}>
          {loading ? '—' : total.toLocaleString('pt-BR')}
        </Typography>
        <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.72rem', color: C.text2 }}>
          mensagens
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.7, minHeight: 22 }}>
        {delta !== null ? (
          <>
            <Tag
              color={delta >= 0 ? C.green : C.red}
              bg={delta >= 0 ? C.greenDim : C.redDim}
            >
              {delta >= 0 ? '▲ +' : '▼ '}{delta}%
            </Tag>
            <Typography sx={{ fontSize: '0.7rem', color: C.text3, fontFamily: FONT_TXT }}>
              vs semana anterior
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '0.7rem', color: C.text3, fontFamily: FONT_TXT }}>
            {loading ? 'carregando…' : isEmpty ? 'sem histórico ainda' : 'primeira semana'}
          </Typography>
        )}
      </Box>

      <Box sx={{ height: 78, mt: 1, mx: -0.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="volGradCur" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity={0.38} />
                <stop offset="100%" stopColor={C.green} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={{ stroke: C.border2, strokeWidth: 1 }}
              contentStyle={{
                background: C.bg3, border: `1px solid ${C.border2}`,
                borderRadius: 6, padding: '4px 8px',
                fontFamily: FONT_TXT, fontSize: '0.7rem',
              }}
              labelStyle={{ color: C.text2, fontSize: '0.62rem', marginBottom: 2 }}
              itemStyle={{ color: C.text, padding: 0 }}
              formatter={(v) => [`${v} msg`, '']}
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload;
                if (!p?.date) return '';
                const d = new Date(p.date + 'T00:00:00');
                return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
              }}
              separator=""
            />
            {/* Série ANTERIOR — linha tracejada discreta, sem fill.
                Serve de baseline visual pra semana atual. */}
            <Area
              type="monotone" dataKey="prev"
              stroke={C.text3} strokeWidth={1.5} strokeDasharray="3 3"
              fill="none" dot={false} isAnimationActive={false}
              connectNulls
            />
            {/* Série ATUAL — área com gradient verde + stroke sólido. */}
            <Area
              type="monotone" dataKey="cur"
              stroke={C.green} strokeWidth={2}
              fill="url(#volGradCur)"
              dot={false} isAnimationActive={false}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}


